import { Box, Button, type ButtonProps, Stack } from '@chakra-ui/react'
import type { IconDefinition } from '@fortawesome/fontawesome-common-types'

import chakraTokenToVar from '@/utils/chakraTokenToVar'
import { noOp } from '@/utils/helpers'

import Icon from '@/components/UI/Icon/Icon'

type IconButtonWithNumberVariant = 'subtle' | 'solid'

const VARIANT_TO_DETAILS: Record<
  IconButtonWithNumberVariant,
  {
    color: ButtonProps['color']
    backgroundColor: ButtonProps['bg']
    highlightedBackgroundColor: ButtonProps['bg']
    hoverBackgroundColor: ButtonProps['bg']
    highlightedHoverBackgroundColor: ButtonProps['bg']
    activeBackgroundColor: ButtonProps['bg']
    borderColor: ButtonProps['borderColor']
    highlightedBorderColor: ButtonProps['borderColor']
    hoverBorder: ButtonProps['border']
    activeBorder: ButtonProps['border']
  }
> = {
  solid: {
    color: 'fg.muted',
    backgroundColor: 'bg.muted',
    highlightedBackgroundColor: 'primary.subtle',
    hoverBackgroundColor: 'bg.emphasized',
    highlightedHoverBackgroundColor: 'primary.subtle',
    activeBackgroundColor: 'bg.emphasized',
    borderColor: 'transparent',
    highlightedBorderColor: 'primary.border',
    hoverBorder: `2px solid ${chakraTokenToVar('border.emphasized')}`,
    activeBorder: `2px solid ${chakraTokenToVar('gray.focusRing')}`,
  },
  subtle: {
    color: 'fg.muted',
    backgroundColor: 'bg',
    highlightedBackgroundColor: 'bg.muted',
    hoverBackgroundColor: 'bg.muted',
    highlightedHoverBackgroundColor: 'bg.emphasized',
    activeBackgroundColor: 'bg.emphasized',
    borderColor: 'transparent',
    highlightedBorderColor: 'transparent',
    hoverBorder: '2px solid transparent',
    activeBorder: '2px solid transparent',
  },
}

export default function IconButtonWithNumber({
  icon,
  onClick = noOp,
  label = null,
  highlighted = false,
  isDisabled = false,
  variant = 'solid',
}: {
  icon: IconDefinition | string
  onClick?: () => void
  label?: number | null
  highlighted?: boolean
  isDisabled?: boolean
  variant?: IconButtonWithNumberVariant
}): JSX.Element {
  const {
    backgroundColor,
    highlightedBackgroundColor,
    borderColor,
    highlightedBorderColor,
    hoverBorder,
    activeBorder,
    hoverBackgroundColor,
    highlightedHoverBackgroundColor,
    activeBackgroundColor,
    color,
  } = VARIANT_TO_DETAILS[variant]

  return (
    <Button
      color={color}
      minWidth="unset"
      backgroundColor={highlighted ? highlightedBackgroundColor : backgroundColor}
      border="2px solid"
      borderColor={highlighted ? highlightedBorderColor : borderColor}
      borderRadius="8px"
      height="32px"
      px="8px"
      onClick={onClick}
      isDisabled={isDisabled}
      _hover={{
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        border: hoverBorder,
        bg: highlighted ? highlightedHoverBackgroundColor : hoverBackgroundColor,
      }}
      _active={{
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        border: activeBorder,
        bg: activeBackgroundColor,
      }}
    >
      <Stack direction="row" align="center" spacing="6px">
        {typeof icon === 'string' ? <Box>{icon}</Box> : <Icon faIcon={icon} size="md" />}
        {label == null ? null : <Box>{label}</Box>}
      </Stack>
    </Button>
  )
}
